import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Button, Card, Checkbox, Form, Input } from 'antd';
import { useLoginMutation } from 'features/auth/authApiSlice';
import { setCredentials } from 'features/auth/authSlice';
import { setAuthToken } from 'shared/helpers/accessTokenHelper';
import { useAppDispatch } from 'shared/hooks/hooks';

import './Login.scss';

interface LoginFormValues {
  username: string;
  password: string;
  rememberUser: boolean;
}

const Login: FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userRef = useRef<HTMLInputElement>(null);
  const errRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

  const { message } = App.useApp();

  const [errorMessage, setErrorMessage] = useState<string>('');

  const [form] = Form.useForm();

  const [login, { isLoading }] = useLoginMutation();

  const showMessage = useCallback(
    (name: string): void => {
      message.success(`Hello, ${name}!`)!;
    },
    [message],
  );

  useEffect(() => {
    userRef.current?.focus();
  }, []);

  useEffect(() => {
    setErrorMessage('');
  }, []);

  const handleSubmit = useCallback(
    async (values: LoginFormValues): Promise<void> => {
      const { username, password, rememberUser } = values;

      try {
        const { access_token: token } = await login({ username, password }).unwrap();
        dispatch(setCredentials({ token, username }));
        setAuthToken(token, rememberUser);
        showMessage(username);

        navigate('/');
      } catch (error) {
        console.log(error);
        setErrorMessage('Login failed');
        errRef.current?.focus();
      }
    },
    [dispatch, login, navigate, showMessage],
  );

  return (
    <div className="h-screen flex flex-col justify-center items-center login-page">
      {isLoading ? (
        <p>loading...</p>
      ) : (
        <Card className="login-card" title="Monitoring service">
          <Form name="basic" initialValues={{ remember: true }} form={form} autoComplete="off" onFinish={handleSubmit}>
            <Form.Item label="Username" name="username" rules={[{ required: true, message: 'Please input your username!' }]}>
              <Input />
            </Form.Item>

            <Form.Item label="Password" name="password" rules={[{ required: true, message: 'Please input your password!' }, { min: 6 }]}>
              <Input.Password />
            </Form.Item>

            <Form.Item name="remember" valuePropName="checked">
              <Checkbox>Remember me</Checkbox>
            </Form.Item>

            <div ref={errRef} className="text-red-500 flex justify-center h-7">
              {errorMessage}
            </div>

            <Form.Item>
              <Button htmlType="submit" block>
                Login
              </Button>
            </Form.Item>
          </Form>
        </Card>
      )}
    </div>
  );
};

export default Login;
